import { useUserStore } from "@/stores";
import { useEffect, useMemo, useRef, useState, useContext } from "react"
import { createBrowserRouter, RouterProvider, RouteObject } from 'react-router-dom'
import Loader from "atol-atoms-2/Loader";
import { KeycloakContext } from "@/app/providers/KeycloakProvider";
import { useDefineRole } from "@/shared/hooks/useDefineRole";
import { Roles } from "@/shared/constants/roles";
import styles from './styles.module.scss'
import { defaultRoutes } from "./constants";
import { normalizePath } from "./utils";

export const RoleBasedRouter = () => {
    const locationRef = useRef(window.location.href)
    const routerRef = useRef<ReturnType<typeof createBrowserRouter> | null>(null)
    const routesLoadedRef = useRef(false)
    const [routes, setRoutes] = useState<RouteObject[] | null>(null)
    const { tokenParsed } = useContext(KeycloakContext)
    const role = useDefineRole(tokenParsed)
    const isLoading = useUserStore((store) => store.isLoading)
    const initialLoad = useUserStore((store) => store.initialLoad)

    const loadRouter = async () => {
        try {
            let loadedRoutes: RouteObject[]

            switch (role) {
                case Roles.Owner: loadedRoutes = await import('@/app/ownerRouter').then(m => m.default); break;
                case Roles.Manager: loadedRoutes = await import('@/app/managerRoutes').then(m => m.default); break;
                case Roles.Administrator: loadedRoutes = await import('@/app/administratorRoutes').then(m => m.default); break;
                case null: loadedRoutes = defaultRoutes; break;
                default: throw new Error(`Неизвестная роль: ${role}`);
            }

            setRoutes(loadedRoutes)
        } catch (e) {
            console.error(e)
            setRoutes(defaultRoutes)
        } finally {
            routesLoadedRef.current = true
        }
    }

    const router = useMemo(() => {
        return routerRef.current = routes === null
            ? null
            : createBrowserRouter(routes)
    }, [routes])

    useEffect(() => {
        if (initialLoad && routesLoadedRef.current) return
        setRoutes(null)
        void loadRouter()
    }, [role, initialLoad])

    useEffect(() => {
        if (!routesLoadedRef.current
            || !routerRef.current
            || routerRef.current.state?.location?.state?.fromAuthRedirect) return

        const originalPath = normalizePath(locationRef.current)
        const currentPath = normalizePath(window.location.href)

        if (originalPath && originalPath !== currentPath && originalPath !== '/') {
            routerRef.current.navigate(originalPath, {
                replace: true,
                state: { fromAuthRedirect: true }
            })
        }
    }, [routes])

    if (isLoading || !initialLoad || !routesLoadedRef.current || !router) return (
        <div className={styles.loaderWrapper}>
            <Loader size={64} />
        </div>
    )

    return <RouterProvider router={router} />
}